import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';

// --- GLOBAL TOAST EMITTER (for use outside React components like api.js) ---
const toastListeners = new Set();

export const toast = {
    show: (message, type = 'info') => {
        toastListeners.forEach(listener => listener(message, type));
    },
    error: (message) => toast.show(message, 'error'),
    success: (message) => toast.show(message, 'success'),
    warning: (message) => toast.show(message, 'warning'),
    info: (message) => toast.show(message, 'info'),
};

// --- TOAST CONTEXT ---
const ToastContext = createContext(null);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

// --- TOAST PROVIDER ---
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'info') => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);

        // Auto-dismiss after 4 seconds
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 4000);
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    // Subscribe to global toast events (for api.js and other non-React code)
    useEffect(() => {
        toastListeners.add(showToast);
        return () => toastListeners.delete(showToast);
    }, [showToast]);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        </ToastContext.Provider>
    );
};

// --- TOAST CONTAINER ---
const ToastContainer = ({ toasts, onDismiss }) => {
    if (toasts.length === 0) return null;

    return (
        <div
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[300] flex flex-col gap-2 w-full max-w-sm px-4"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
            {toasts.map(toast => (
                <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
            ))}
        </div>
    );
};

// --- SINGLE TOAST ---
const Toast = ({ toast, onDismiss }) => {
    const { id, message, type } = toast;

    const config = {
        success: {
            bg: 'bg-green-50 border-green-200',
            icon: CheckCircleIcon,
            iconColor: 'text-green-500',
            text: 'text-green-800'
        },
        error: {
            bg: 'bg-red-50 border-red-200',
            icon: ExclamationCircleIcon,
            iconColor: 'text-red-500',
            text: 'text-red-800'
        },
        warning: {
            bg: 'bg-yellow-50 border-yellow-200',
            icon: ExclamationTriangleIcon,
            iconColor: 'text-yellow-600',
            text: 'text-yellow-800'
        },
        info: {
            bg: 'bg-blue-50 border-blue-200',
            icon: InformationCircleIcon,
            iconColor: 'text-blue-500',
            text: 'text-blue-800'
        }
    };

    const { bg, icon: Icon, iconColor, text } = config[type] || config.info;

    return (
        <div
            className={`${bg} border rounded-xl shadow-lg p-3 flex items-start gap-3 animate-slide-down`}
            role="alert"
        >
            <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
            <p className={`${text} text-sm font-medium flex-1`}>{message}</p>
            <button
                onClick={() => onDismiss(id)}
                className={`${text} opacity-50 hover:opacity-100 transition-opacity flex-shrink-0`}
            >
                <XMarkIcon className="w-4 h-4" />
            </button>
        </div>
    );
};

export default Toast;
